<template>
  <div class="md-layout md-size-100" v-if="loaded">
    <div
      id="left-half"
      class="md-layout-item md-size-75 md-medium-size-100 md-small-size-100"
    >
      <ValidationObserver ref="validationObserver" v-slot="{ validate }">
        <div class="item-container md-layout">
          <md-icon v-if="working" class="text-info status-icon working"
            >cached</md-icon
          >
          <md-icon v-else class="text-success status-icon done">done</md-icon>
          <div
            class="md-layout md-layout-item md-medium-size-100"
            v-for="(params, index) in paramGroups"
            :key="index"
            :id="'item-ui-' + index"
            :class="index !== 0 ? 'md-size-50 no-right-padding' : 'md-size-100'"
          >
            <md-card>
              <md-card-header>
                <div class="md-size-100 text-center">
                  <h3 class="section-title">
                    {{ params.title }}
                  </h3>
                </div>
              </md-card-header>
              <md-card-content class="md-layout">
                <div
                  class="md-layout-item md-medium-size-100"
                  v-for="(param, paramIndex) in params.params"
                  :key="paramIndex"
                  :class="param.size ? 'md-size-' + param.size : 'md-size-100'"
                >
                  <ValidationProviderExtended
                    v-if="param.type === 'select'"
                    :name="param.label"
                    rules="required"
                    className="md-warning"
                  >
                    <label>{{ param.label }}</label>
                    <md-select
                      v-model="item[param.name]"
                      @input="
                        sendParam(param);
                        validate();
                      "
                    >
                      <md-option
                        v-for="(option, index) in param.options"
                        :key="index"
                        :value="option.id"
                        >{{ option.name }}</md-option
                      >
                    </md-select>
                  </ValidationProviderExtended>

                  <ValidationProviderExtended
                    v-else-if="param.type === 'text'"
                    :name="param.label"
                    rules="required|min:2"
                    className="md-warning"
                  >
                    <label>{{ param.label }}</label>
                    <md-input
                      v-model="item[param.name]"
                      type="text"
                      @blur="sendParam(param)"
                      @focus="setWorking"
                    ></md-input>
                  </ValidationProviderExtended>
                  <ValidationProvider
                    v-else-if="param.type === 'dropdown'"
                    :ref="`${param.name}DropdownValProvider`"
                    rules="required|min_value:1"
                    v-slot="{ invalid, passed }"
                  >
                    <drop-down direction="down" multiLevel>
                      <md-button
                        slot="title"
                        class="dropdown-toggle item-dropdown"
                        :class="[
                          item[param.name] ? 'md-primary' : 'md-inactive',
                        ]"
                        data-toggle="dropdown"
                      >
                        <span :id="param.name">{{
                          param.label +
                          (param.selected ? ': ' + param.selected.label : '')
                        }}</span>
                        <slide-y-down-transition>
                          <md-icon v-show="invalid">close</md-icon>
                        </slide-y-down-transition>
                        <slide-y-down-transition>
                          <md-icon v-show="passed">done</md-icon>
                        </slide-y-down-transition>
                      </md-button>
                      <ul class="dropdown-menu">
                        <li
                          v-for="(group, groupIndex) in param.options"
                          :key="groupIndex"
                          @click="toggleMultiLevel(paramIndex, groupIndex)"
                        >
                          <a
                            class="dropdown-toggle"
                            :class="{
                              open:
                                multiLevel === `${paramIndex}-${groupIndex}`,
                            }"
                            >{{ group.group }}</a
                          >
                          <ul class="dropdown-menu dropdown-menu-right">
                            <li
                              v-for="(option, index) in group.list"
                              :key="index"
                              @click="
                                selectOption(
                                  param.name,
                                  option.id,
                                  param.label + ': ' + option.label,
                                )
                              "
                            >
                              <a>{{ option.label }}</a>
                            </li>
                          </ul>
                        </li>
                      </ul>
                    </drop-down>
                  </ValidationProvider>
                  <input-plus
                    v-else-if="param.type === 'button_checkbox'"
                    class="md-layout-item md-size-100 md-disabled"
                    :values="{
                      defaultValue: false,
                      checkAgainst: item[param.name],
                    }"
                    :title="param.label"
                    direction="up"
                    @close-dropdown="sendParam(param)"
                    @open-dropdown="sendParam(param)"
                  >
                    <md-button
                      class="checkbox-button"
                      :class="[
                        item[param.name]
                          ? 'selected-button-checkbox'
                          : 'deselected-button-checkbox',
                      ]"
                      :disabled="!ready"
                      @click="toggleChecked(param.name)"
                      slot="title"
                    >
                      <md-icon
                        :class="[
                          item[param.name] ? 'md-primary' : 'md-inactive',
                        ]"
                      >
                        {{ item[param.name] ? 'done' : 'add' }}
                      </md-icon>
                      {{ param.label }}
                      <span
                        v-if="param.additionalParameters && item[param.name]"
                      >
                        <span
                          v-for="(
                            addParam, index
                          ) in param.additionalParameters.flat()"
                          :key="index"
                        >
                          <span
                            class="button-params"
                            v-if="addParam.type === 'number'"
                          >
                            {{ `${addParam.label}` }} {{ item[addParam.name] }}
                          </span>
                          <span
                            class="button-params"
                            v-else-if="
                              addParam.type === 'radio' &&
                              paramRequired(addParam)
                            "
                          >
                            {{ getParamLabels(addParam) }}
                          </span>
                        </span>
                      </span>
                    </md-button>
                    <ul
                      v-if="param.additionalParameters"
                      class="dropdown-menu dropdown-menu-right"
                    >
                      <li
                        v-for="(addParam, index) in param.additionalParameters"
                        :key="index"
                        class="md-layout no-wrap"
                      >
                        <div v-if="paramRequired(addParam[0])">
                          <div
                            class="md-layout-item"
                            v-for="(column, index) in addParam"
                            :key="index"
                          >
                            <div
                              v-if="column.type === 'number'"
                              class="md-layout-item"
                            >
                              <md-field>
                                <label>{{ column.label }}</label>
                                <md-input
                                  v-model="item[column.name]"
                                  type="number"
                                  :step="column.step"
                                  :value="column.selected"
                                  :min="column.min"
                                >
                                </md-input>
                              </md-field>
                            </div>
                            <div
                              v-else-if="column.type === 'radio'"
                              class="md-layout md-layout-item"
                            >
                              <md-field>
                                <div
                                  v-for="(option, index) in column.options"
                                  :key="index"
                                  class="md-layout-item"
                                >
                                  <md-radio
                                    v-model="item[column.name]"
                                    :value="option.name"
                                  >
                                    {{ option.label }}
                                  </md-radio>
                                </div>
                              </md-field>
                            </div>

                            <div
                              v-else-if="column.type === 'checkbox'"
                              class="md-layout md-layout-item"
                            >
                              <md-field>
                                <md-checkbox v-model="item[column.name]">{{
                                  column.label
                                }}</md-checkbox>
                              </md-field>
                            </div>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </input-plus>
                  <ValidationProviderExtended
                    v-else-if="param.type === 'number'"
                    :name="param.label"
                    :rules="[
                      'non_negative',
                      param.address === 'jobItemPriceCalculation' && 'required',
                      param.min && 'min_value:' + param.min,
                      param.max && 'max_value:' + param.max,
                    ]"
                  >
                    <label>
                      {{ param.label }}
                    </label>
                    <md-input
                      v-model="item[param.name]"
                      type="number"
                      :min="param.min"
                      :max="param.max"
                      :disabled="!ready && param.address === 'jobItemOptional'"
                      @blur="
                        sendParam(param);
                        validate();
                      "
                      @focus="setWorking"
                    >
                    </md-input>
                  </ValidationProviderExtended>

                  <div
                    v-else-if="param.type === 'checkbox'"
                    class="md-layout-item"
                  >
                    <md-checkbox
                      v-model="item[param.name]"
                      @change="
                        sendParam(param);
                        validate();
                      "
                    >
                      {{ param.label }}
                    </md-checkbox>
                  </div>
                  <md-field
                    class="md-layout"
                    v-else-if="param.type === 'radio'"
                  >
                    <label class="radio-title md-layout-item md-size-100">
                      {{ param.label }}
                    </label>
                    <div class="md-layout-item">
                      <md-radio
                        v-for="(option, index) in param.options"
                        v-model="item[param.name]"
                        :key="index"
                        :checked="option.name === param.selected"
                        @change="
                          sendParam(param);
                          validate();
                        "
                        :value="option.name"
                      >
                        {{ option.label }}
                      </md-radio>
                    </div>
                  </md-field>
                </div>
              </md-card-content>
            </md-card>
          </div>
        </div>
      </ValidationObserver>
    </div>
    <div id="right-half" class="md-layout-item md-size-25 md-medium-size-100">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-brand">
          <div class="card-icon">
            <md-icon>assignment</md-icon>
          </div>
          <h3 class="title">Repromaterijal</h3>
        </md-card-header>
        <md-card-content>
          <md-table :value="materialData" class="table-striped">
            <md-table-row slot="md-table-row" slot-scope="{ item }">
              <md-table-cell md-label="Materijal"
                ><b>{{ item.label }}</b></md-table-cell
              >
              <md-table-cell md-label="Količina">{{
                item.amount
              }}</md-table-cell>
              <md-table-cell md-label="JM">{{ item.unit }}</md-table-cell>
            </md-table-row>
          </md-table>
        </md-card-content>
      </md-card>
    </div>
  </div>
</template>

<script>
import api from '../api/api.js';
import services from '@/api/services';
import ValidationProviderExtended from '@/components/ValidationProviderExtended.vue';
import { SlideYDownTransition } from 'vue2-transitions';
export default {
  components: {
    ValidationProviderExtended,
    SlideYDownTransition,
  },
  props: ['index'],
  data() {
    return {
      loaded: false,
      paramGroups: null,
      multiLevel: null,
      // dropDownOpen: [],
      materialData: null,
      working: false,
      ready: false,
      item: {
        name: null,
        paperTypeId: null,
        paperGroup: null,
        inputArchFormatId: null,
        itemFormatId: null,
        broughtPlates: null,
        broughtPaper: null,
        broughtPrintedPaper: null,
        colorPaletteId: null,
        colorPaletteBackId: null,
        purpose: null,
        printMachineId: null,
        printDifficulty: null,
        foldingWithGlue: null,
        inserting: null,
        goldPrint: null,
        goldPrintType: null,
        goldPrintBroughtTool: null,
        goldPrintHeight: null,
        goldPrintWidth: null,
        cutting: null,
        numOfCuts: null,
        plasticType: null,
        offsetPolish: null,
        offsetPolishSides: null,
        offsetPolishFinish: null,
        plastic: null,
        softPlasticSides: null,
        softPlasticFinish: null,
        machineFolding: null,
        bigs: null,
        perforation: null,
        punch: null,
        punchType: null,
        punchToolCost: null,
      },
    };
  },
  methods: {
    paramRequired(param) {
      if (param.require) {
        switch (param.require.function) {
          case 'equals':
            if (this.item[param.require.variable] === param.require.compare)
              return true;
            else return false;

          default:
            return true;
        }
      } else {
        return true;
      }
    },
    toggleChecked(param) {
      this.item[param] = !this.item[param];
    },
    toggleMultiLevel(paramIndex, index) {
      this.multiLevel = `${paramIndex}-${index}`;
    },
    setWorking() {
      window.setTimeout(() => {
        this.working = true;
      }, 80);
    },
    selectOption(param, id) {
      this.$refs[`${param}DropdownValProvider`][0].validate(id);
      this.item[param] = id;
      this.sendParam({ address: 'jobItemPriceCalculation' });
    },
    getParamLabels(param) {
      if (param.options) {
        return param.options.find((el) => {
          return el.name === this.item[param.name];
        }).label;
      } else {
        return null;
      }
    },
    sendParam(param = null) {
      this.working = true;
      const requestParams = {};

      if (param.address === 'jobItemPriceCalculation' || param === null) {
        requestParams.mainPriceCalc = true;

        /** Fill requestParams object with main params */
        this.paramGroups[0].params.forEach((element) => {
          if (
            element.address === 'jobItemPriceCalculation' &&
            this.item[element.name] !== null
          ) {
            requestParams[element.name] = this.item[element.name];
          }
        });

        api.post(services.item.jobItemPriceCalculation, requestParams).then(
          (data) => {
            if (data.itemParams) {
              this.paramGroups = data.itemParams;
            }
            if (data.item) {
              if (data.item.ready) {
                this.ready = true;
                this.$emit('ready-to-save', true);
              }
              for (const [key, value] of Object.entries(data.item)) {
                if (
                  typeof value === 'object' &&
                  !['materialTable', 'prices'].includes(key)
                ) {
                  for (const [lowerKey, lowerValue] of Object.entries(value)) {
                    if (
                      this.item[lowerKey] !== undefined &&
                      lowerKey !== 'name'
                    )
                      this.item[lowerKey] = lowerValue;
                  }
                } else if (this.item[key] !== undefined) {
                  this.item[key] = value;
                }
              }
            }
            if (data.materialTable) {
              this.materialData = Object.values(data.materialTable);
            }
            this.$emit('new-calculation', data);
            this.working = false;
            this.validateDropdowns();
          },
          (error) => {
            this.alertError(error);
          },
        );
      } else if (param.address === 'jobItemOptional' && this.ready) {
        requestParams.option = param.name;
        requestParams[param.name] = this.item[param.name];
        if (this.item[param.name] && param.additionalParameters) {
          param.additionalParameters.flat().forEach((element) => {
            if (this.paramRequired(element)) {
              requestParams[element.name] = this.item[element.name];
            }
          });
        }
        api.post(services.item.jobItemOptional, requestParams).then(
          (data) => {
            if (data.materialTable) {
              this.materialData = Object.values(data.materialTable);
            }
            this.$emit('new-calculation', data.itemTable);
            this.working = false;
          },
          (error) => {
            this.alertError(error);
          },
        );
      }
    },
    refreshItemParams() {
      return api.get(services.item.getItemParams, { getItemParams: true }).then(
        (data) => {
          this.paramGroups = data.itemParams;
          this.paramGroups.forEach((params) => {
            params.params.forEach((param) => {
              if (['dropdown', 'select'].includes(param.type) && param.selected)
                this.item[param.name] = param.selected.id;
              else this.item[param.name] = param.selected;
              if (param.additionalParameters) {
                param.additionalParameters.forEach((row) => {
                  row.forEach((addParam) => {
                    this.item[addParam.name] = addParam.selected;
                  });
                });
              }
            });
          });
          this.loaded = true;
        },
        (error) => {
          this.alertError(error);
        },
      );
    },
    getItem() {
      if (this.index !== null && this.index !== -1) {
        api
          .post(services.item.editCachedItem, {
            editItem: true,
            itemIndex: this.index,
          })
          .then(
            (data) => {
              if (data.item && data.item.materialTable) {
                this.materialData = Object.values(data.item.materialTable);
              }
              this.refreshItemParams().then(() => {
                this.sendParam({ address: 'jobItemPriceCalculation' });
              });
            },
            (error) => {
              this.alertError(error);
            },
          );
      } else {
        this.refreshItemParams().then(() => {
          this.sendParam({ address: 'jobItemPriceCalculation' });
        });
      }
    },
    validateDropdowns() {
      this.$refs.paperTypeIdDropdownValProvider[0].validate(
        this.item.paperTypeId,
      );
      this.$refs.inputArchFormatIdDropdownValProvider[0].validate(
        this.item.inputArchFormatId,
      );
    },
  },
  mounted() {
    this.$parent.$on('submited-job', () => {
      this.sendParam({ address: 'jobItemPriceCalculation' });
    });
    this.getItem();
  },
  destroyed() {
    this.$parent.$off();
  },
  watch: {
    index: {
      handler() {
        this.getItem();
      },
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/md/_variables.scss';
.no-wrap {
  flex-wrap: nowrap !important;
  padding: 5px;
}
.status-icon {
  position: absolute;
  right: 20px;
  top: 20px;
  z-index: 1200;
}
.working {
  animation: rotate 1s linear infinite;
}
.item-dropdown {
  position: relative;
  width: 100%;
  min-width: 120px;
}
.dropdown-menu-right {
  max-height: 483px;
  overflow-y: auto;
  /* box-shadow: inset 0px -10px 10px -6px rgba(150, 150, 150, 50); */
}
.dropdown-menu-right::-webkit-scrollbar {
  display: none;
}
.item-container {
  position: relative;
}
.section-title {
  position: relative;
  top: -20px;
}
.md-card {
  padding: 10px 0px;
  margin: 0 0 15px 0 !important;
  z-index: auto;
}
.no-right-padding {
  padding: 0px;
}
.md-layout-item {
  padding: 0px 5px !important;
}
.checkbox-button.selected-button-checkbox {
  background-position-x: 85%;
}
.checkbox-button.selected-button-checkbox:hover {
  background-position-x: right;
}
.checkbox-button.deselected-button-checkbox,
.checkbox-button.deselected-button-checkbox i {
  background-position-x: left;
  color: $brand-primary !important;
}
.md-input {
  width: 100%;
  z-index: 0;
}
.md-field::after {
  z-index: 0 !important;
}
.checkbox-button {
  background: linear-gradient(
    90deg,
    rgba(133, 133, 133, 1) 0%,
    rgba(186, 186, 186, 1) 50%,
    rgba(55, 71, 79, 1) 83%,
    rgba(23, 48, 61, 1) 100%
  );
  background-size: 1000%;

  transition: all 0.6s ease-in-out;
  position: relative;
  width: 100%;
  min-width: 120px;
}
.radio-title {
  margin: 7px;
  font-size: 1.5em;
  position: relative !important;
  display: block;
  color: #393939 !important;
}
.button-params {
  padding: 0px 5px;
}
@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }
  50% {
    transform: rotate(-180deg);
  }
  100% {
    transform: rotate(-360deg);
  }
}
</style>
